import type { ReactNode } from 'react';
import { spinIconOut } from '../utils/iconAnimations.ts';
import '../styles/buttonAnimations.css';

// ─── Shared floating action button ─────────────────────────────────────────────────────────────
// Extracted from Learning Hub's "Upload to Learning Hub" FAB (the first/only floating button in
// the app) so other pages (e.g. Inventory Catalog's "Add Item") can reuse the exact same fixed
// bottom-right circular shell + combo-jello-spin bounce/spin mechanics instead of hand-copying
// the inline styles. Learning Hub's own FAB now renders through this component unchanged.
// tint defaults to 'blue' (the original, only color this shell ever rendered) -- 'red' added for
// the batch-delete FAB (Inventory.tsx's supply grids, item 5) per BUTTON_SYSTEM.md's own
// Delete/Remove data-tint mapping (red, #f87171 / rgba(239,68,68,...)); buttonAnimations.css
// already had a data-tint="red" hover rule defined (matching that same doc), just never used by
// this component yet since nothing floating had needed a destructive variant until now.
// 'neutral' added for the "Deselect All" FAB (Inventory.tsx, item 2) -- BUTTON_SYSTEM.md's own
// neutral/gray data-tint (used for Edit/Archive elsewhere), a non-destructive, non-primary action
// that shouldn't compete visually with red (delete) or blue (the primary Add action).
const TINTS = {
  blue: { bg: 'rgba(59,130,246,0.16)', border: '1px solid rgba(59,130,246,0.45)', color: '#93c5fd' },
  red: { bg: 'rgba(239,68,68,0.16)', border: '1px solid rgba(239,68,68,0.45)', color: '#f87171' },
  neutral: { bg: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: '#cbd5e1' },
} as const;

export function FloatingActionButton({
  icon,
  onClick,
  ariaLabel,
  title,
  bottom = 31,
  right = 28,
  tint = 'blue',
}: {
  icon: ReactNode;
  onClick: () => void;
  ariaLabel: string;
  title?: string;
  bottom?: number;
  right?: number;
  tint?: keyof typeof TINTS;
}) {
  const c = TINTS[tint];
  return (
    <button
      className="combo-jello-spin"
      data-tint={tint}
      onMouseLeave={spinIconOut}
      onClick={onClick}
      title={title}
      aria-label={ariaLabel}
      style={{
        position: 'fixed', bottom, right, zIndex: 500,
        width: 48, height: 48, borderRadius: 999,
        background: c.bg, border: c.border,
        color: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
      }}
    >
      {icon}
    </button>
  );
}
